/**
 * Money and quantity arithmetic.
 *
 * INVARIANT: money never touches a float. Amounts are integers in MINOR units
 * (fils; 1 AED = 100 fils) and carry a `Minor` type. Quantities are integers
 * scaled by 1000 (`Milli`) so 2.5 litres of oil is 2500.
 *
 * These functions are pure and have no I/O. They are the most heavily tested
 * code in the project because a silent error here costs real money.
 */

/** An integer amount of money in minor units (hundredths of the currency unit). */
export type Minor = number
/** An integer quantity scaled by 1000. */
export type Milli = number

/** Storage precision is fixed: 100 minor units per major unit, always. */
export const MINOR_PER_MAJOR = 100
/** Quantity scale: 1000 milli-units per whole unit. */
export const MILLI_PER_UNIT = 1000

/**
 * Round to the nearest integer, halves away from zero.
 *
 * JavaScript's Math.round is half-UP (towards +Infinity), which rounds -0.5 to
 * -0 rather than -1 and so is asymmetric for credits/negative adjustments.
 */
export function roundHalfUp(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value)
}

/** Total for one line: quantity x unit rate, rounded to the nearest minor unit. */
export function lineTotalMinor(quantityMilli: Milli, rateMinor: Minor): Minor {
  return roundHalfUp((quantityMilli * rateMinor) / MILLI_PER_UNIT)
}

/** Sum of minor amounts. Kept as a named function so intent is greppable. */
export function sumMinor(values: readonly Minor[]): Minor {
  return values.reduce((acc, v) => acc + v, 0)
}

/** Percentage of `part` relative to `whole`, to one decimal place. 0 when whole is 0. */
export function percentOf(part: number, whole: number): number {
  if (whole === 0) return 0
  return Math.round((part / whole) * 1000) / 10
}

export interface FormatMoneyOptions {
  /** Currency symbol or code to prefix, e.g. "AED". Omitted when undefined. */
  symbol?: string
  /** Display decimal places, 0-2. Storage is always hundredths regardless. */
  decimals?: number
  /** Insert thousands separators. Default true. */
  grouping?: boolean
}

/** Render a minor amount for display. Never used for arithmetic. */
export function formatMinor(minor: Minor, options: FormatMoneyOptions = {}): string {
  const { symbol, decimals = 2, grouping = true } = options
  const negative = minor < 0
  const abs = Math.abs(minor)
  const major = Math.floor(abs / MINOR_PER_MAJOR)
  const rest = abs % MINOR_PER_MAJOR

  let intPart = String(major)
  if (grouping) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  let text = intPart
  if (decimals > 0) {
    const fraction = String(rest).padStart(2, '0').slice(0, decimals)
    text = `${intPart}.${fraction}`
  }
  if (negative) text = `-${text}`
  return symbol ? `${symbol} ${text}` : text
}

/**
 * Parse user input into minor units. Accepts "1,234.56", "1234.5", "1234".
 * Returns null for anything unparseable so callers must handle bad input.
 */
export function parseMoneyToMinor(input: string): Minor | null {
  const cleaned = input.trim().replace(/,/g, '')
  if (cleaned === '' || !/^-?\d*(\.\d{0,})?$/.test(cleaned)) return null
  const value = Number(cleaned)
  if (!Number.isFinite(value)) return null
  return roundHalfUp(value * MINOR_PER_MAJOR)
}

/** Render a milli quantity, trimming trailing zeros: 2500 -> "2.5", 1000 -> "1". */
export function formatMilli(milli: Milli): string {
  const negative = milli < 0
  const abs = Math.abs(milli)
  const whole = Math.floor(abs / MILLI_PER_UNIT)
  const rest = abs % MILLI_PER_UNIT
  let text = String(whole)
  if (rest > 0) text += `.${String(rest).padStart(3, '0').replace(/0+$/, '')}`
  return negative ? `-${text}` : text
}

/** Parse a quantity into milli units. Returns null when unparseable. */
export function parseQuantityToMilli(input: string): Milli | null {
  const cleaned = input.trim().replace(/,/g, '')
  if (cleaned === '' || !/^-?\d*(\.\d{0,})?$/.test(cleaned)) return null
  const value = Number(cleaned)
  if (!Number.isFinite(value)) return null
  return roundHalfUp(value * MILLI_PER_UNIT)
}
